// Display formatters for campaign metrics. Keeping them centralized prevents
// drift between the list, detail, and summary surfaces.

import { CHANNEL, OFFER } from '../constants';

const formatNumber = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US').format(Math.round(n));
};

const formatPercent = (n, digits = 1) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
};

const formatCurrency = (n, currency = 'USD') => {
  if (n == null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
};

const formatRelativeMinutes = (minutes) => {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / (60 * 24))}d`;
};

export const metricFormatters = {
  number: formatNumber,
  percent: formatPercent,
  currency: formatCurrency,
  relativeMinutes: formatRelativeMinutes,
};

// Ordered list of metric descriptors per (channels, offer) combination.
// Detail page MetricsGrid and list row secondary metric both read from here.
export const metricsFor = ({ channels = [], offer = OFFER.NONE } = {}) => {
  const hasEmail = channels.includes(CHANNEL.EMAIL);
  const hasUpsell = channels.includes(CHANNEL.NETWORK_UPSELL);

  if (hasEmail && hasUpsell) {
    return [
      { key: 'recipients', label: 'Recipients', channel: CHANNEL.EMAIL, format: 'number', source: 'metrics.recipients' },
      { key: 'openRate', label: 'Open rate', channel: CHANNEL.EMAIL, format: 'percent', source: 'metrics.openRate' },
      { key: 'impressions', label: 'Impressions', channel: CHANNEL.NETWORK_UPSELL, format: 'number', source: 'metrics.impressions' },
      { key: 'converted', label: 'Conversions', channel: 'aggregate', format: 'number', source: 'metrics.converted' },
    ];
  }

  if (hasEmail) {
    if (offer === OFFER.INVITATION) {
      return [
        { key: 'recipients', label: 'Sent', format: 'number', source: 'metrics.recipients' },
        { key: 'converted', label: 'Accepted', format: 'number', source: 'metrics.converted' },
        { key: 'acceptRate', label: 'Accept rate', format: 'percent', source: 'metrics.acceptRate', compute: (m) => m.recipients ? (m.converted / m.recipients) * 100 : 0 },
        { key: 'clicked', label: 'Clicks', format: 'number', source: 'metrics.clicked' },
      ];
    }
    if (offer === OFFER.DISCOUNT) {
      return [
        { key: 'recipients', label: 'Sent', format: 'number', source: 'metrics.recipients' },
        { key: 'converted', label: 'Redeemed', format: 'number', source: 'metrics.converted' },
        { key: 'redemptionRate', label: 'Redemption', format: 'percent', source: 'metrics.redemptionRate', compute: (m) => m.recipients ? (m.converted / m.recipients) * 100 : 0 },
        { key: 'revenue', label: 'Revenue', format: 'currency', source: 'metrics.revenue' },
      ];
    }
    return [
      { key: 'recipients', label: 'Recipients', format: 'number', source: 'metrics.recipients' },
      { key: 'opened', label: 'Opens', format: 'number', source: 'metrics.opened' },
      { key: 'clicked', label: 'Clicks', format: 'number', source: 'metrics.clicked' },
      { key: 'openRate', label: 'Open rate', format: 'percent', source: 'metrics.openRate' },
    ];
  }

  if (hasUpsell) {
    return [
      { key: 'impressions', label: 'Impressions', format: 'number', source: 'metrics.impressions' },
      { key: 'uniqueImpressions', label: 'Unique', format: 'number', source: 'metrics.uniqueImpressions' },
      { key: 'clicked', label: 'Clicks', format: 'number', source: 'metrics.clicked' },
      { key: 'converted', label: 'Enrollments', format: 'number', source: 'metrics.converted' },
    ];
  }

  return [];
};

// Primary + secondary metric for the list row "Performance" cell.
export const rowMetrics = (campaign) => {
  const channels = campaign?.channels ?? [];
  const metrics = campaign?.metrics ?? {};
  const offer = campaign?.offer;

  if (channels.includes(CHANNEL.EMAIL) && offer === OFFER.INVITATION) {
    const acceptRate = metrics.recipients ? (metrics.converted / metrics.recipients) * 100 : 0;
    return {
      primary: { label: 'accepted', value: formatNumber(metrics.converted), rate: formatPercent(acceptRate) },
      secondary: 'acceptance',
    };
  }
  if (channels.includes(CHANNEL.EMAIL) && offer === OFFER.DISCOUNT) {
    return {
      primary: { label: 'redemption', value: formatPercent(metrics.clickRate || 0), rate: formatCurrency(metrics.revenue, 'USD') },
      secondary: 'revenue',
    };
  }
  if (channels.includes(CHANNEL.EMAIL)) {
    return {
      primary: { label: 'open', value: formatPercent(metrics.openRate), rate: `${formatPercent(metrics.ctr)} CTR` },
      secondary: null,
    };
  }
  if (channels.includes(CHANNEL.NETWORK_UPSELL)) {
    return {
      primary: { label: 'impressions', value: formatNumber(metrics.impressions), rate: formatNumber(metrics.clicked) + ' clicks' },
      secondary: null,
    };
  }
  return { primary: { label: '', value: '—', rate: '' }, secondary: null };
};
